// src/calcRules.js
// Pure pricing rules the calculator (views/Calc.jsx) runs on. They live here,
// out of the component, so each one is pinned by a test
// (src/__tests__/calc.test.js) instead of only being checked by eye.

import { HUD_PLF, HUD_PLF_MIN_RATE, HUD_PLF_MAX_RATE } from './hudPlfTable';
import { talcAge, quoteDate, FOA_LTV_CUT } from './plfTables';
import { hecmLimitForYear } from './constants';

// ── HECM principal limit factor — HUD's official table ──────────────────
// Expected rate rounds to the NEAREST 1/8 (3.000 floor, 18.875 ceiling) and
// the age is HUD's: age at closing, +1 when the next birthday is under 183
// days away (talcAge). Ages past 99 use 99. Reproduces all 28 PLFs Reece read
// off the lender software on 10/1/26.
export function hudExpectedRate(expRate){
  var r=Math.round((Number(expRate)||0)*8)/8;
  return Math.min(Math.max(r,HUD_PLF_MIN_RATE),HUD_PLF_MAX_RATE);
}
export function hudPlf(age,expRate){
  var a=Math.min(99,Math.max(62,Math.round(Number(age)||0)));
  var r=hudExpectedRate(expRate);
  return HUD_PLF[a][Math.round((r-HUD_PLF_MIN_RATE)*8)];
}
// The age the PLF is looked up at. The age>=62 ELIGIBILITY gate stays on the
// borrower's completed age — a 61.6-year-old rounds up to 62 here but still
// can't close a HECM.
export function hecmPlfAge(dob,completedAge,asOf){
  var t=dob?talcAge(dob,asOf):0;
  return t||completedAge||0;
}

// ── HECM upfront MIP ────────────────────────────────────────────────────
// First-time reverse: 2% of the MCA, added to the card's costs.
// Refi of ANY existing reverse (HECM or proprietary): the calculator adds NO
// upfront MIP. Reece puts it in the Estimated Closing Costs (10/2/26: "I
// account for the MIP when I'm estimating the closing costs"), so adding it
// here would count it twice. HUD's HECM-to-HECM credit math was overkill
// and is gone. The card says where the MIP is (`inCC`) instead of a bare $0;
// with no closing-cost estimate typed (`ccEntered` false) it says the MIP
// isn't counted anywhere yet (`missing`).
export var REFI_MIP_LABEL="Upfront MIP: in your Estimated Closing Costs";
export var REFI_MIP_MISSING_LABEL="Upfront MIP not counted — no closing-cost estimate entered. Put the MIP in Estimated Closing Costs.";
export function hecmUpfrontMip(mcaNew,o){
  o=o||{};
  if(o.isRefi){
    var missing=o.ccEntered===false;
    var r={mip:0,basis:"refi",inCC:true,label:missing?REFI_MIP_MISSING_LABEL:REFI_MIP_LABEL};
    if(missing)r.missing=true;
    return r;
  }
  return{mip:(Number(mcaNew)||0)*0.02,basis:"first",label:"2% of MCA"};
}

// ── HECM year-1 cash (HUD Initial Disbursement Limit) ──────────────────
// Closing + first 12 months can't exceed the LESSER of
//   (a) the greater of 60% of PL, or mandatory obligations + 10% of PL, and
//   (b) PL minus the LESA.
// Mandatory obligations = payoff + financed costs (MIP, origination, closing).
// Year-1 CASH is what's left of that limit after the mandatory obligations.
export function hecmInitialDisbursementLimit(pl,mand,lesa){
  pl=Number(pl)||0;mand=Number(mand)||0;
  return Math.max(0,Math.min(Math.max(0.6*pl,mand+0.1*pl),pl-(Number(lesa)||0)));
}
export function hecmYear1Cash(pl,mand,lesa,avail){
  var idl=hecmInitialDisbursementLimit(pl,mand,lesa);
  return Math.max(0,Math.min(Number(avail)||0,idl-(Number(mand)||0)));
}

// ── Cash to borrower, from components ──────────────────────────────────
// Recomputed from scratch after every adjustment (LESA, removed origination,
// borrower credit) so a credit can never land on a value already clamped at
// $0 and make an underwater deal look viable. `shortfall` is what the
// borrower would have to bring to close.
export function settleAvail(p){
  var credit=Number(p.credit)||0;
  var net=(Number(p.gross)||0)-(Number(p.tc)||0)+credit;
  var raw=p._second?net:net-(Number(p.payoff)||0)-(Number(p.lesa)||0);
  return{net:p._second?Math.max(0,net):net,avail:Math.max(0,raw),shortfall:raw<0?-raw:0,positive:raw>0};
}

// ── Origination ────────────────────────────────────────────────────────
// Fixed proprietary (origToLender): the origination is the LENDER's fee and
// is priced into the rate tier, so origFor always returns it in full. Lenders
// don't do partial origination cuts (Reece, 10/1/26): an Origination Override
// below it leaves the lender's orig, price and comp exactly as priced, and the
// difference is credited to the borrower out of Reece's pay
// (origOverrideCredit), never more than Reece's comp on the card (capCredits). The
// Remove-origination toggle is the only way to drop it entirely, and the
// lender takes 100 bps off the price for that.
// Broker ARMs (Select Intro, SecureEquity ARM, Platinum LOC) default to $0;
// HECMs default to the max. The override caps both — that orig is Reece's own.
export function origFor(o){
  var maxO=Number(o.maxO)||0;
  if(o.origToLender)return maxO;
  if(o.defaultZero)return o.ooSet?Math.min(Number(o.ooN)||0,maxO):0;
  return o.ooSet?Math.min(Number(o.ooN)||0,maxO):maxO;
}
// Remove origination on a fixed proprietary product: origination → $0 and the
// price −1.00, comp recomputed at the lower price (the sheet's minimum comp
// still applies). Returns null when it does not apply — including when there
// was no origination to remove, which must NOT cost 100 bps.
export function removeOrigination(p){
  if(!p||!p.origToLender||!(p._prem>0)||!(p._upb>0))return null;
  var o=Number(p.orig)||0;
  if(!(o>0))return null;
  var was=p._prem,np=+(was-1).toFixed(3);
  var base=p._upb*Math.max(0,np-100)/100,minC=p._minComp||0;
  var comp=Math.max(minC,base);
  return{orig:0,tc:(Number(p.tc)||0)-o,_prem:np,comp:comp,lenderPaid:comp,waived:true,removedOrig:o,wasPrem:was,minHit:!!(minC&&base<minC)};
}
// Origination Override on a fixed proprietary card → borrower credit out of
// Reece's pay: the lender's orig minus the override (floored at $0). The
// lender's orig, price and comp are NOT touched — Calc takes this off the comp
// and adds it to `credit`, the same way as Credit to Borrower. 0 when the
// override is blank or at/above the lender's orig (it can't charge more), when
// the card isn't fixed proprietary (there the override caps Reece's own orig
// — origFor), or when Remove origination already took this card's orig off.
// `comp` (optional) = what Reece still earns on the card after incentives and
// Credit to Borrower: a credit "for my pay" can't be more than the pay, so the
// credit is capped there (never below $0). A credit under $1 is no credit.
export function origOverrideCredit(o){
  o=o||{};
  if(!o.origToLender||!o.ooSet||o.waived)return 0;
  var orig=Number(o.orig)||0,want=Math.max(0,Number(o.ooN)||0);
  var d=orig>want?orig-want:0;
  if(o.comp!=null)d=Math.min(d,Math.max(0,Number(o.comp)||0));
  return Math.round(d)>0?d:0;
}
// ── Borrower credits never go past the comp ────────────────────────────
// Reece, 10/1/26: "The credit can never go past the comp. That would not
// ever be viable or realistic." Every borrower credit on a card comes out of
// Reece's pay, so the TOTAL is capped at the comp left on the card after
// incentives (a $0 or negative comp has nothing to give). Credit to Borrower
// (`credit`) comes out first; the fixed-prop override credit (`origCredit`,
// what origOverrideCredit says uncapped) gets what's left. A credit under $1
// is no credit (as in origOverrideCredit), and so is a shortfall under $1 —
// float noise must not show "$0 not covered". Returns what is APPLIED (only
// that ever reaches the borrower's cash), what is NOT covered, and the comp
// after: credits never move it below $0 — a comp already negative for some
// other reason (a pricing hit on a flat-comp product) is left as it was.
export function capCredits(o){
  o=o||{};
  var comp=Number(o.comp)||0,avail=Math.max(0,comp);
  var want=Math.max(0,Number(o.credit)||0),wantO=Math.max(0,Number(o.origCredit)||0);
  var whole=function(v){return Math.round(v)>0?v:0;};
  var credit=whole(Math.min(want,avail));
  var origCredit=whole(Math.min(wantO,avail-credit));
  return{credit:credit,origCredit:origCredit,
    creditShort:whole(want-credit),origCreditShort:whole(wantO-origCredit),
    total:credit+origCredit,compAfter:comp-credit-origCredit};
}
// What the Remove-origination toggle would cost Reece on a card: comp at the
// sheet price minus comp at −1.00 (the sheet's minimum comp still applies).
// `incBps` = the off-sheet incentive bps that apply to the card: each moves
// the price, and YSP is only paid above par, so with a pricing hit near par
// the 100-bps cut costs less than 100 bps of YSP. p.comp is the comp BEFORE
// incentives. null when the toggle wouldn't apply to the card.
export function removeOriginationCost(p,incBps){
  var r=removeOrigination(p);if(!r)return null;
  var upb=Number(p._upb)||0;
  var inc=function(prem){var s=0;(incBps||[]).forEach(function(b){b=Number(b)||0;s+=upb*(Math.max(0,prem+b/100-100)-Math.max(0,prem-100))/100;});return s;};
  return Math.max(0,((Number(p.comp)||0)+inc(p._prem))-(r.comp+inc(r._prem)));
}

// ── Prior HECM: what the recorded lien says about the house back then ──
// A HECM records at 150% of its max claim, so lien ÷ 1.5 = the MCA at
// origination = the LESSER of the appraised value then and that year's FHA
// limit. Under the limit it IS the appraised value at that point in time; at
// the limit (within 1%) the home was worth at least that. Wording only — the
// New Call card's recorded-lien line (CallConsole lienNote) reads with it;
// the calculator takes no prior MCA (no H2H MIP credit since 10/2/26).
// `close` is any text with a 4-digit year in it ("2015", "6/2015", "2015-06-01").
// Before 2009 there was no single limit: HECM limits were set by COUNTY, up
// to a high-cost ceiling of 87% of the conforming limit (PRE_HERA_CEILING),
// then HERA made it $417,000 nationwide (11/2008) until ARRA's $625,500
// (2/17/2009). So a pre-2009 MCA is the appraised value OR that county's
// limit, and only the ceiling / $417,000 can be read as "capped". (Not
// hecmLimitForYear's job: the Outbound filter wants $625,500 for those years.)
// An MCA over every limit its year allowed means the year or lien is wrong.
var PRE_HERA_CEILING={2004:290319,2005:312895,2006:362790,2007:362790,2008:362790};
var HERA_LIMIT=417000;
export function priorMcaMeaning(mca,close,currentLimit){
  var m=Number(mca)||0;if(!(m>0))return null;
  var ym=/(\d{4})/.exec(String(close==null?"":close));var yr=ym?parseInt(ym[1],10):0;
  if(yr&&yr>new Date().getFullYear()+1)yr=0;
  var near=function(l){return !!(l&&Math.abs(m-l)/l<0.01);};
  var res=function(lim,capped,text,anomaly){var r={year:yr||null,limit:lim||null,capped:capped,text:text};if(anomaly)r.anomaly=true;return r;};
  var over=function(lim){return res(lim,false,"more than the "+yr+" FHA limit ("+money(lim)+") — check the year or the lien amount",true);};
  if(!yr)return res(null,false,"the appraised value back then, or that year's FHA limit if the home was worth more");
  if(yr>=2009){
    var lim=hecmLimitForYear(yr,Number(currentLimit)||null)||0;
    if(near(lim))return res(lim,true,"the "+yr+" FHA limit, so the home appraised at "+money(lim)+" or more");
    if(yr===2009&&near(HERA_LIMIT))return res(HERA_LIMIT,true,"the $417,000 FHA limit (11/2008–2/2009), so the home appraised at $417,000 or more");
    if(lim&&m>lim*1.01)return over(lim);
    return res(lim,false,"the appraised value in "+yr);
  }
  var ceil=PRE_HERA_CEILING[yr]||0;
  if(yr===2008&&near(HERA_LIMIT))return res(HERA_LIMIT,true,"the $417,000 FHA limit (11/2008–2/2009), so the home appraised at $417,000 or more");
  if(near(ceil))return res(ceil,true,"the "+yr+" FHA ceiling for high-cost counties, so the home appraised at "+money(ceil)+" or more");
  var top=yr===2008?HERA_LIMIT:ceil;
  if(top&&m>top*1.01)return over(top);
  // Over the county ceiling in 2008 → it closed under HERA's national limit.
  if(yr===2008&&m>ceil*1.01)return res(HERA_LIMIT,false,"the appraised value in 2008");
  return res(ceil,false,"the appraised value in "+yr+", or that county's FHA limit if the home was worth more (limits were set by county before 2009)");
}

// ── Refi benefit tests, per lender ─────────────────────────────────────
// Only the rules the lender documents give numbers for:
//  • FOA (HECM + HomeSafe): PL increase ≥ 3 × costs (3–5× = Team Lead review),
//    and new PL − payoff − costs ≥ 5% of new PL. (HECM UW manual p182-183,
//    HomeSafe manual p103-104, Bulletin 20.)
//  • SmartFi (HECM + Choice): PL increase ≥ 5 × costs (IMIP included — on a
//    refi it's inside Reece's closing-cost estimate), and
//    new PL − payoff − costs ≥ 5% of new PL. (HECM guide p24-25, Choice §3.12.)
//  • Longbridge (HECM + Platinum): PL increase ≥ 5 × costs, and
//    new PL − existing balance ≥ 5% of new PL. (HECM guide p69-75, Platinum p87-88.)
//  • Mutual of Omaha: no lender document on file — the legacy test stays
//    (added cash ≥ 3 × costs) and the card says so.
// "costs" are net of any borrower credit. Prior PL comes from refiPrior: the
// Current Principal Limit on an adjustable loan, the balance on a fixed one,
// else the balance (labelled as an estimate).
export var REFI_RULES={
  foa:{mult:3,review:5,proceedsNetOfCosts:true,name:"FOA 3:1"},
  smartfi:{mult:5,proceedsNetOfCosts:true,name:"SmartFi 5×"},
  longbridge:{mult:5,proceedsNetOfCosts:false,name:"Longbridge 5×"},
};
export function lenderKey(lender){
  var s=String(lender||"");
  if(/Finance of America|\bFOA\b/i.test(s))return"foa";
  if(/SmartFi/i.test(s))return"smartfi";
  if(/Longbridge/i.test(s))return"longbridge";
  if(/Mutual of Omaha|\bMoO\b/i.test(s))return"moo";
  return"";
}
function money(n){var v=Math.round(Number(n)||0);return(v<0?"−$":"$")+Math.abs(v).toLocaleString();}
export function refiBenefit(o){
  var costs=Math.max(0,Number(o.costs)||0);
  var k=lenderKey(o.lender),r=REFI_RULES[k];
  if(!r){
    var added=(Number(o.avail)||0)-(Number(o.curAvail)||0);
    var okL=added>=3*costs;
    return{passes:okL,rule:"legacy",review:false,
      label:(k==="moo"?"MoO":"This lender")+": no lender doc on file — legacy test (added cash "+money(added)+(okL?" ≥ ":" < ")+"3× costs "+money(3*costs)+")"};
  }
  var newPL=Number(o.newPL)||0,priorPL=Number(o.priorPL)||0,payoff=Number(o.payoff)||0;
  var inc=newPL-priorPL;
  var ratio=costs>0?inc/costs:(inc>0?Infinity:0);
  var ccOk=costs>0?inc>=r.mult*costs:inc>0;
  var left=newPL-payoff-(r.proceedsNetOfCosts?costs:0);
  var pct=newPL>0?left/newPL:0;
  var procOk=pct>=0.05;
  var passes=ccOk&&procOk;
  var review=!!(passes&&r.review&&ratio<r.review);
  var label=r.name+" — PL "+(inc>=0?"+":"")+money(inc)+(inc>0&&isFinite(ratio)?" = "+ratio.toFixed(1)+"× costs":"")+(ccOk?"":" (needs "+r.mult+"× costs)")+
    " · proceeds "+(pct*100).toFixed(1)+"% of PL"+(procOk?"":" (needs 5%)")+
    (review?" · 3–5× = Team Lead review":"")+(o.priorPLEstimated?" · "+PRIOR_PL_ESTIMATED:"");
  return{passes:passes,rule:k,ratio:ratio,proceedsPct:pct,ccOk:ccOk,procOk:procOk,review:review,label:label};
}
// ── The existing reverse on a refi (Reece, 10/2/26) ────────────────────
// "just the balance and current principal limit. and if it's a fixed
// program the current principal limit would be irrelevant." No current
// available funds / LOC input any more. What the benefit tests compare
// against:
//   • Fixed existing loan: a lump sum with no line of credit — the prior PL
//     IS the balance (not an estimate), nothing available today.
//   • Adjustable with the Current Principal Limit: that PL; what's available
//     today ≈ PL − balance (MoO's legacy cash test reads it).
//   • Adjustable, PL not entered: the balance, labelled as an estimate.
export var PRIOR_PL_ESTIMATED="prior PL estimated as the balance — enter the current principal limit";
export function refiPrior(o){
  o=o||{};
  var bal=Math.max(0,Number(o.balance)||0),pl=Math.max(0,Number(o.currentPL)||0);
  if(o.existingFixed)return{priorPL:bal,estimated:false,curAvail:0,basis:"fixed"};
  if(pl>0)return{priorPL:pl,estimated:false,curAvail:Math.max(0,pl-bal),basis:"pl"};
  return{priorPL:bal,estimated:true,curAvail:0,basis:"balance"};
}
// Does the linked record say the EXISTING reverse is a fixed-rate loan? Read
// off what the record says about the loan they already have — the statement
// fields, an explicit flag, the lead card's meta, or a mail-list lead's
// loan_type ("Fixed", "HECM Fixed"). A pipeline file's own `type` is the NEW
// loan (HECM / Jumbo …) and is never read as the existing one.
// What Reece last PICKED wins over every guess (existingLoanChoice): the
// choice is kept as "fixed" / "adjustable" on the calc-lead shape
// (prospectToCalcLead), the card's meta (a file's leadMeta, a mail-list
// lead's profile meta — handleQuoteRun / the calculator's pitch), the
// pitched deal and the last scenario, so reopening the numbers — New Call's
// "← Edit the deal", a re-price — reopens on it instead of Adjustable.
var _pickExisting=function(v){var s=String(v==null?"":v).trim().toLowerCase();return s==="fixed"||s==="adjustable"?s:"";};
export function existingLoanChoice(rec){
  if(!rec||typeof rec!=="object")return"";
  var hist=Array.isArray(rec.history)?rec.history:[],prof=null;
  for(var i=hist.length-1;i>=0;i--){if(hist[i]&&hist[i].type==="profile"){prof=hist[i].data||{};break;}}
  var sh=Array.isArray(rec.scenarioHistory)?rec.scenarioHistory:[];
  var last=sh.length?sh[sh.length-1]:null;
  return _pickExisting(rec.existingLoan)
    ||_pickExisting(rec.leadMeta&&rec.leadMeta.existingLoan)
    ||_pickExisting(prof&&prof.existingLoan)
    ||_pickExisting(rec.deal&&rec.deal.existingLoan)
    ||_pickExisting(last&&last.existingLoan);
}
export function existingLoanIsFixed(rec){
  if(!rec||typeof rec!=="object")return false;
  var said=function(v){return /\bfixed\b/i.test(String(v==null?"":v));};
  if(rec.existingFixed===true)return true;
  var picked=existingLoanChoice(rec);
  if(picked)return picked==="fixed";
  if(said(rec.existingLoan)||said(rec.existingRateType))return true;
  var sd=rec.statementData||rec.statement_data;
  if(sd&&typeof sd==="object"&&(said(sd.loanType)||said(sd.rateType)||said(sd.existingLoan)||said(sd.type)))return true;
  var meta=rec.leadMeta;
  if(meta&&typeof meta==="object"&&(said(meta.existingLoan)||said(meta.rateType)))return true;
  if(said(rec.loan_type))return true;
  if(rec._marketingOnly&&said(rec.type))return true;
  return false;
}

// ── Deal builder: per-card adjustments (Reece, 10/2/26) ────────────────
// "the options like Origination override, credit to borrower, and all that
// stuff, i want to have that as an option as I'm choosing the deal" — every
// per-deal knob (Origination override, Credit to Borrower, Remove
// origination, the cash draw) lives on ONE card's deal builder, keyed the way
// the Lender's amount is (lenderAmountKey), and applies to that card only;
// every other card stays at standard pricing. Calc feeds the normalized
// entry to the same rules the global inputs used (origFor, removeOrigination,
// origOverrideCredit, capCredits), so the math is the one copy it always was.
// `entry` = {origOvr, credit, waive, drawMode, customDraw, oldLtv} as the
// builder stored it (typed amounts as text). Blank override = not set (the
// card's default orig); "0" = set to $0. `oldLtv` = FOA Bulletin 33's
// "application taken before 10/3 — keep old LTVs" switch (HomeSafe Std /
// Intro / Select Intro, 10/3–10/31/26; plfTables FOA_LTV_CUT) — default off.
export var DRAW_MODES=["min","max","custom"];
export function dealAdjustment(entry){
  var e=entry||{};
  var digits=function(v){return parseInt(String(v==null?"":v).replace(/[^0-9]/g,""),10)||0;};
  var oo=String(e.origOvr==null?"":e.origOvr).trim();
  var ooSet=oo!=="";
  var mode=DRAW_MODES.indexOf(e.drawMode)>=0?e.drawMode:"min";
  var r={ooSet:ooSet,ooN:ooSet?digits(oo):0,credit:digits(e.credit),waive:!!e.waive,drawMode:mode,customCash:mode==="custom"?digits(e.customDraw):0,oldLtv:!!e.oldLtv};
  r.active=r.ooSet||r.credit>0||r.waive||r.drawMode!=="min"||r.oldLtv;
  return r;
}
// The cash draw only moves comp on a line-of-credit / adjustable product —
// every HECM and the adjustable proprietary programs (HomeSafe Select Intro,
// SecureEquity ARM, Choice Flex, Platinum LOC: "ARM" in the name). A fixed
// lump sum always draws the full amount.
export function drawApplies(p){
  if(!p||p.origToLender)return false;
  return !!p._hecm||/ARM/i.test(String(p.name||""));
}
// What a card's deal builder changed, for the records (deal.adjustments, the
// quote snapshot): only the knobs that applied to THIS card. null when none.
export function adjustmentRecord(p,a){
  if(!p||!a||!a.active)return null;
  var r={};
  // The override applied where there's an origination to cap (HECM / broker
  // ARM) or a lender's orig still on the card to credit against (fixed —
  // not once Remove origination took it off).
  var ovrApplies=p.origToLender?(!p.waived&&(Number(p.orig)||0)>0):(Number(p.maxOrig)||0)>0;
  if(a.ooSet&&ovrApplies)r.origOverride=a.ooN;
  if(a.credit>0)r.creditToBorrower=a.credit;
  if(p.waived)r.removeOrigination=true;
  if(drawApplies(p)&&a.drawMode!=="min")r.draw=a.drawMode==="custom"?{mode:"custom",cash:a.customCash}:{mode:"max"};
  // Old LTVs: only where it priced this card — a HomeSafe Std / Intro /
  // Select Intro card the Bulletin 33 cut applies to, inside 10/3–10/31/26
  // (p._oldLtv, set by the card's proprietaryPLF). A stored flag outside
  // that window, or on any other card, is ignored and never recorded.
  if(a.oldLtv&&p._oldLtv)r.oldLtv=true;
  return Object.keys(r).length?r:null;
}

// ── FOA Bulletin 33: was this file priced on a HomeSafe before 10/3? ────
// The deal builder's old-LTV switch shows a one-line hint when the linked
// pipeline file's CURRENT deal is a HomeSafe Standard / Intro / Select
// Intro (the calculator's "HomeSafe Std @ …" or a hand-entered "HomeSafe
// Standard") and either
//   • ANY scenario-history entry on one of those three programs is dated
//     before the cut — the EARLIEST one is the date (FOA may well have had
//     the application since then; a later re-price, even one at the cut,
//     doesn't change when the file started), or
//   • the current deal already says it was priced at the old LTVs
//     (adjustments.oldLtv, or ltvRule "pre-10/3 LTVs") — re-pitched with the
//     switch on, so reopening the file must keep reminding (the switch
//     itself always comes back OFF).
// A hint only — the switch stays off until Reece turns it on.
var FOA_CUT_PRODUCT=/^HomeSafe (Std|Standard|Intro|Select Intro)\b/;
function foaCutEntry(x){
  return !!x&&FOA_CUT_PRODUCT.test(String(x.product||""))&&!(x.lender&&!/Finance of America/.test(String(x.lender)));
}
// A history entry's local calendar day: its timestamp, else its date text;
// null when neither reads as a date (never "today").
function dayOf(v){
  if(v==null||v==="")return null;
  if(typeof v==="string"&&/^\d{4}-\d{2}-\d{2}$/.test(v))return v;
  var t=new Date(v);
  return isNaN(t.getTime())?null:quoteDate(t);
}
function historyDay(h){return dayOf(h.timestamp)||dayOf(h.date);}
// → null, or {pricedOn, oldLtv, oldLtvOn}: `pricedOn` = the earliest
// pre-10/3 day (YYYY-MM-DD) the file was priced on one of the three
// programs (null when none); `oldLtv` = the current deal records old LTVs;
// `oldLtvOn` = the day of the newest entry for the current deal's product
// (when it recorded them; else the deal's enteredAt, else null).
export function foaFileLtvHint(rec){
  if(!rec||typeof rec!=="object")return null;
  var d=rec.deal;
  if(!foaCutEntry(d))return null;
  var first=null,last=null;
  var sh=Array.isArray(rec.scenarioHistory)?rec.scenarioHistory:[];
  for(var i=0;i<sh.length;i++){
    var h=sh[i];if(!foaCutEntry(h))continue;
    var day=historyDay(h);if(!day)continue;
    if(day<FOA_LTV_CUT.effective&&(!first||day<first))first=day;
    if(h.product===d.product&&(!last||day>last))last=day;
  }
  var kept=!!(d.adjustments&&d.adjustments.oldLtv)||d.ltvRule===FOA_LTV_CUT.ruleOld;
  if(!first&&!kept)return null;
  var on=null;
  if(kept)on=last||(d.enteredAt?historyDay({timestamp:d.enteredAt}):null);
  return{pricedOn:first,oldLtv:kept,oldLtvOn:on};
}
// The hint's date (YYYY-MM-DD) or null: the earliest pre-10/3 pricing, else
// (a deal that records old LTVs) the day it was pitched that way.
export function foaPricedBeforeCut(rec){
  var h=foaFileLtvHint(rec);
  return h?(h.pricedOn||h.oldLtvOn||null):null;
}

// ── State / age eligibility for proprietary products ───────────────────
// Applied whenever Property State is set — a blank state means no rules.
// There is NO license gate (Reece, 10/1/26: "Don't worry about the license
// thing and hard stops, I want to always be able to run deals no matter
// what"): every state prices, and BRAND.licensedStates is compliance text
// only. These are the LENDERS' rules, so they follow the property wherever
// it is, and they never hide a program for good — the calculator flags it
// (see the eligibility flags below) and "Show programs that don't fit"
// prices it anyway.
//   TX: 62+ on every proprietary product. WA: 60+.
//   HomeSafe Standard: 60+ in MA/NY, 62+ in NC. HomeSafe Intro: 60+ in MA, 62+ in NC.
//   HomeSafe Second: not in GA / TN / VA (Bulletin 32, 9/25/26).
//   Choice Fixed: not in VA. Choice Flex: only AZ CA CO FL GA TX UT.
//   Select Intro, Platinum LOC and Platinum Preserve: not in WA.
export var PROP_STATE_MIN_AGE={TX:62,WA:60};
var FAMILY_STATE_MIN_AGE={hsStd:{MA:60,NY:60,NC:62},hsIntro:{MA:60,NC:62}};
var NOT_OFFERED={hs2:["GA","TN","VA"],choiceFixed:["VA"],hsSelIntro:["WA"],platLoc:["WA"],platPreserve:["WA"]};
var ONLY_IN={choiceFlex:["AZ","CA","CO","FL","GA","TX","UT"]};
var FAMILY_NAME={hecm:"HECM",hsStd:"HomeSafe Standard",hsIntro:"HomeSafe Intro",hsSelIntro:"HomeSafe Select Intro",hs2:"HomeSafe Second",
  seFixed:"SecureEquity Fixed",seFixedPlus:"SecureEquity Fixed Plus",seArm:"SecureEquity ARM",choiceFixed:"SmartFi Choice Fixed",
  choiceFlex:"SmartFi Choice Flex",platFixed:"Platinum Fixed",platPreserve:"Platinum Preserve",platLoc:"Platinum LOC"};
export function familyName(f){return FAMILY_NAME[f]||f;}
// The lender's state rule for this family, short (the card already names the
// program): "Not offered in VA", "Only offered in AZ/CA/…", "TX requires age 62+".
function stateRule(family,state,age){
  var st=String(state==null?"":state).trim().toUpperCase();
  if(!/^[A-Z]{2}$/.test(st))return null;
  if((NOT_OFFERED[family]||[]).indexOf(st)>=0)return{kind:"notOffered",short:"Not offered in "+st,st:st};
  var only=ONLY_IN[family];
  if(only&&only.indexOf(st)<0)return{kind:"onlyIn",short:"Only offered in "+only.join("/"),st:st,only:only};
  var min=Math.max(PROP_STATE_MIN_AGE[st]||0,((FAMILY_STATE_MIN_AGE[family]||{})[st])||0);
  if(min&&(Number(age)||0)<min)return{kind:"age",short:st+" requires age "+min+"+",st:st,min:min};
  return null;
}
// The same rule as a sentence (kept for the "why" lists and older callers).
export function propStateGate(family,state,age){
  var r=stateRule(family,state,age);if(!r)return null;
  var nm=familyName(family);
  if(r.kind==="notOffered")return nm+" is not offered in "+r.st;
  if(r.kind==="onlyIn")return nm+" is only offered in "+r.only.join("/");
  return r.st+" requires age "+r.min+"+ for "+nm;
}

// ── Eligibility FLAGS — never a hard stop ──────────────────────────────
// Reece, 10/1/26: "always make sure there's an option to override … There's
// sometimes one offs and I don't want the system to outsmart me." A lender
// rule the scenario breaks becomes a FLAG on the card instead of deleting the
// card: the default view still hides it (Reece wanted a clean list), the
// "Show programs that don't fit" switch prices and shows it with these tags,
// sorted after the viable ones, and it can be pitched — the pitch records
// the override (pitchOverrides). Each flag: {code, tag (on the card),
// override (what the pitched deal records), red (the pricing basis itself
// was substituted)}.
// Hard rules that STAY hard: no LESA on SmartFi Choice or HomeSafe Second
// (lesaGate), and borrower credits never past the comp (capCredits) — a
// card priced on the lender's own figure (applyLenderAmount) included.
export var HECM_MIN_AGE=62;
export var PROP_MIN_AGE=55;
export var HECM_UNDER_AGE_TAG="Priced at 62 — below HUD's 62 minimum; a younger non-borrowing spouse lowers the real PLF";
export function hecmAgeFlag(age){
  if((Number(age)||0)>=HECM_MIN_AGE)return null;
  return{code:"age",red:true,tag:HECM_UNDER_AGE_TAG,override:"below HUD's 62 minimum — priced at 62"};
}
export function propAgeFlag(age){
  if((Number(age)||0)>=PROP_MIN_AGE)return null;
  return{code:"age",red:true,tag:"Priced at the lender table's lowest age — below the 55 program minimum",override:"below the 55 program minimum — priced at the lender table's lowest age"};
}
export function propStateFlag(family,state,age){
  var r=stateRule(family,state,age);if(!r)return null;
  return{code:"state",tag:r.short,override:propStateGate(family,state,age)};
}
// FOA HomeSafe Standard / Intro / Select Intro: PL $200K or more.
export function hsMinPlFlag(gross){
  var g=Number(gross)||0;if(g>=200000)return null;
  return{code:"minPL",tag:"PL "+money(g)+" is under FOA's $200K minimum",override:"PL under FOA's $200K minimum"};
}
// HomeSafe Second: $50K minimum ($70K in Utah — FAR footnote 7).
export function hs2MinFlag(amount,state){
  var min=String(state||"").trim().toUpperCase()==="UT"?70000:50000;
  var a=Number(amount)||0;if(a>=min)return null;
  return{code:"minPL",tag:"Amount "+money(a)+" is under HomeSafe Second's "+money(min)+" minimum"+(min===70000?" (UT)":""),override:"under HomeSafe Second's "+money(min)+" minimum"};
}
// HomeSafe Second sits BEHIND a forward first mortgage that stays in place.
// In the calculator's HomeSafe Second mode with no first-mortgage balance
// entered it still prices (no hard stops) — flagged, since a free-and-clear
// home is a HomeSafe Standard / Intro deal, not a second.
export function hs2NoFirstFlag(firstLien){
  if((Number(firstLien)||0)>0)return null;
  return{code:"noFirst",tag:"No first mortgage entered — HomeSafe Second is a 2nd lien behind one",override:"no first mortgage behind the HomeSafe Second"};
}

// ── HomeSafe Second mode (Reece, 10/2/26) ──────────────────────────────
// "Until I can figure out the CLTV items for HomeSafe Second … manually enter
// the HomeSafe Second amount, calculate the comp on that number and let me
// pitch it." Calc's scenario type "hs2" prices ONLY the HomeSafe Second
// tiers; the amount FOA approved is typed once ("HomeSafe Second amount (from
// FOA)") and stored as the Lender's amount on EVERY tier card — keyed the way
// the cards are (lenderAmountKey), so the card labels, the credit cap and the
// pitch record all treat it as the lender's figure it is.
export var HS2_LENDER="Finance of America";
// The card name every HomeSafe Second tier prices under: "HomeSafe 2nd @ 9.490%".
export function hs2CardName(rate){return "HomeSafe 2nd @ "+Number(rate).toFixed(3)+"%";}
// The Lender's-amount keys of every tier on the HomeSafe Second sheet
// ("9.490/9.990" → two keys), in sheet order. No sheet → [].
export function hs2TierKeys(sheet){
  var out=[];
  String((sheet&&sheet.tiers)||"").split("/").forEach(function(t){
    var r=parseFloat(t);if(isNaN(r))return;
    var k=lenderAmountKey({lender:HS2_LENDER,name:hs2CardName(r)});
    if(out.indexOf(k)<0)out.push(k);
  });
  return out;
}
// The one FOA amount on every tier: the amount when every tier card carries
// the SAME lender figure, "" otherwise (none typed, or tiers typed apart on
// their own cards). `amts` = Calc's {cardKey: {amount, sig}}.
export function hs2SharedAmount(amts,keys){
  var a=amts||{},ks=keys||[];
  if(!ks.length)return"";
  var first=a[ks[0]]&&a[ks[0]].amount;
  if(!first)return"";
  return ks.every(function(k){return a[k]&&String(a[k].amount)===String(first);})?String(first):"";
}
// What an HS2 card on the calculator's own estimate says about itself — on
// the card, in its terms (and so every quote / pitch record) and the deal.
export var HS2_ESTIMATE_TAG="Estimated — confirm in FOA's software";
// ── HomeSafe Second mode = PL in, pay out (Reece, 10/10/26) ────────────
// "for Homesafe seconds, remove the ability to enter in the closing costs, or
// anything like that. No option for credits either … Just give me the
// ability to enter in the PL and then calculate the pay based off that."
// The mode's only input is FOA's amount; the pay is the sheet's premium on
// it, never under the minimum comp (SC: none — FAR footnote 8).
// Pay with cents, as the pay is quoted ("$7,437.50").
export function payCents(n){
  var v=Number(n)||0;
  return(v<0?"−$":"$")+Math.abs(v).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});
}
// The pay line under an HS2 card's pay: "Prem 103.5 on $212,500 =
// $7,437.50", or "… = $4,200.00 → $5,000.00 minimum" when the minimum comp
// is what pays. o = {prem, amount, minComp}.
export function hs2PayDetail(o){
  o=o||{};
  var prem=Number(o.prem)||0,amt=Number(o.amount)||0,min=Number(o.minComp)||0;
  var onPrem=amt*Math.max(0,prem-100)/100;
  return "Prem "+prem+" on "+money(amt)+" = "+payCents(onPrem)+(min>onPrem?" → "+payCents(min)+" minimum":"");
}
// The lender rules an HS2 card in that mode can still break — warnings, never
// a stop: not offered in the state (GA / TN / VA), the $50K ($70K UT)
// minimum on the amount, and the age minimums ONLY when the record has a DOB
// (the mode has no DOB box, so an unknown age never reads as "under 55").
// No "no first mortgage entered" flag: there is no balance box to enter one in.
export function hs2PayFlags(o){
  o=o||{};
  var age=Number(o.age)||0,out=[];
  if(age>0){out.push(propAgeFlag(age));out.push(propStateFlag("hs2",o.state,age));}
  else{var r=stateRule("hs2",o.state,0);if(r&&r.kind!=="age")out.push(propStateFlag("hs2",o.state,0));}
  out.push(hs2MinFlag(o.amount,o.state));
  return out.filter(Boolean);
}
// SecureEquity (MoO) and Platinum (Longbridge): $300K minimum home value.
export function minHomeValueFlag(hv,min){
  var m=Number(min)||300000;if((Number(hv)||0)>=m)return null;
  return{code:"minValue",tag:"Home value under the "+money(m)+" minimum",override:"home value under the "+money(m)+" minimum"};
}
// Every reason a card doesn't fit, for the tag strip: the lender-rule flags,
// a failed benefit test, and no positive cash. [{text, red, code}]
export function misfitReasons(p){
  p=p||{};
  var out=(p.flags||[]).map(function(f){return{code:f.code,text:f.tag,red:!!f.red};});
  if(p.bt&&p.passes===false)out.push({code:"benefit",text:"Fails "+p.bt.label,red:false});
  if(p.positive===false)out.push({code:"cash",text:(Number(p.shortfall)||0)>0?"Short to close "+money(p.shortfall):"No cash after costs",red:false});
  return out;
}
// Does the card clear every lender rule, the benefit test and net cash?
export function isViable(p){return !!p&&!(p.flags&&p.flags.length)&&p.passes!==false&&p.positive!==false;}
// What a pitch made from this card overrode — recorded on the deal and the
// scenario (deal.overrides, the terms text). Empty when it fits.
// A card priced on the lender's own figure (applyLenderAmount) records that
// too: "lender amount", plus a re-check note when the DOB / value / balance
// changed after the figure was typed.
export var LENDER_AMOUNT_OVERRIDE="lender amount";
export var LENDER_AMOUNT_RECHECK="lender amount entered before the DOB / value / balance changed — re-check";
export function pitchOverrides(p){
  p=p||{};
  var out=[];
  if(p.bt&&p.passes===false)out.push("benefit test");
  (p.flags||[]).forEach(function(f){if(f&&f.override&&out.indexOf(f.override)<0)out.push(f.override);});
  if(p.positive===false)out.push((Number(p.shortfall)||0)>0?"short to close "+money(p.shortfall):"no cash after costs");
  if(p.lenderAmount!=null){out.push(LENDER_AMOUNT_OVERRIDE);if(p.lenderStale)out.push(LENDER_AMOUNT_RECHECK);}
  return out;
}

// ── The lender's amount (Reece, 10/1/26) ───────────────────────────────
// Every card takes an optional "Lender's amount": the principal limit (or,
// on HomeSafe Second, the loan amount) Reece read off the lender's own
// software. It REPLACES the card's computed figure at the source — Calc
// hands it to the product block in place of its own gross (lenderGross), so
// the block's origination rules, closing costs, draw / UPB, premium band,
// minimum comp and amount flags all re-figure from it, and so do the passes
// after it (LESA, Remove origination, incentives, capCredits, settleAvail,
// year 1, the benefit test). No second copy of the pricing math exists for
// it. A lender figure is Reece's entry, not a derived value, so the card,
// the terms and a pitch always label it as one (applyLenderAmount,
// pitchOverrides).
// Real case it was built on: HomeSafe Second, DOB 10/10/1948, $850,000
// value, $336,178 first lien, 9.49% — the calculator said $170,000 (the
// age-table 20%), FOA approved $212,500 (25% of value, CLTV .6455).
//
// Keyed per card: lender + product name (the name carries the rate or
// margin), e.g. "Finance of America|HomeSafe 2nd @ 9.490%" — the same string
// as the card's data-card attribute.
export function lenderAmountKey(p){p=p||{};return String(p.lender||"")+"|"+String(p.name||"");}
// What a figure was entered against: the inputs that move every price
// (DOB, home value, balance). A change to any of them KEEPS the figure but
// flags it "re-check" — it was the lender's number for the old scenario,
// and throwing it away would lose something Reece typed off another screen.
export function lenderAmountSig(o){
  o=o||{};
  return [String(o.dob||""),Math.round(Number(o.hv)||0),Math.round(Number(o.mb)||0)].join("|");
}
// The gross a card prices on. `entry` = {amount, sig} as the card's input
// stored it (amount may carry commas / a $). No entry, or not a positive
// amount → the calculator's own figure, untouched. Returns
// {gross, lender, amount, calc, stale}: `stale` when the entry's sig isn't
// the scenario's current one.
export function lenderGross(calcGross,entry,sig){
  var calc=Number(calcGross)||0;
  var amt=entry?parseFloat(String(entry.amount==null?"":entry.amount).replace(/[^0-9.]/g,"")):NaN;
  if(!(amt>0))return{gross:calc,lender:false,calc:calc};
  var stale=sig!=null&&entry.sig!=null&&String(entry.sig)!==String(sig);
  return{gross:amt,lender:true,amount:amt,calc:calc,stale:stale};
}
// A typed lender figure in whole dollars: "$212,500.00" → 212500,
// "212500.5" → 212501. Commas, a $ and cents are read as money — the box
// once stripped the "." and priced "212,500.00" as $21,250,000. Not a
// positive number → 0 (which clears the card's figure).
export function lenderAmountDollars(v){
  var n=parseFloat(String(v==null?"":v).replace(/[^0-9.]/g,""));
  return n>0?Math.round(n):0;
}
// HomeSafe Second's combined LTV: (first lien + this loan) ÷ value. null
// without a value.
export function cltvOf(firstLien,amount,hv){
  var v=Number(hv)||0;if(!(v>0))return null;
  return((Number(firstLien)||0)+(Number(amount)||0))/v;
}
// As the lender software shows it: .6455 (four places, no leading zero).
export function cltvText(r){
  if(r==null||isNaN(Number(r)))return"—";
  var s=Number(r).toFixed(4);
  return s.charAt(0)==="0"?s.slice(1):s;
}
// Label a card the block already priced on the lender's figure (`lg` from
// lenderGross): what was typed, what the calculator said, the PLF that
// figure implies (the calculator's own kept as calcPlf), and the terms text
// every record carries — so a quote or pitch never reads as a calculator
// number. `ctx.firstLien` / `ctx.hv` (HomeSafe Second) refresh the CLTV.
// Mutates and returns the card, like the other passes; a card that isn't on
// a lender figure comes back untouched.
export function applyLenderAmount(p,lg,ctx){
  if(!p||!lg||!lg.lender)return p;
  ctx=ctx||{};
  p.lenderAmount=lg.amount;
  p.calcGross=lg.calc;
  if(lg.stale)p.lenderStale=true;else delete p.lenderStale;
  if(p.plf!=="N/A"&&Number(p.mca)>0){
    if(p.calcPlf==null)p.calcPlf=p.plf;
    p.plf=(lg.amount/Number(p.mca)*100).toFixed(1);
  }
  if(p._second&&Number(ctx.hv)>0)p.cltv=cltvOf(ctx.firstLien,lg.amount,ctx.hv);
  p.lenderNote="Lender figure: "+money(lg.amount)+" (calculator said "+money(lg.calc)+")";
  p.terms=(p.terms||"")+" | Lender's amount "+money(lg.amount)+" (calculator "+money(lg.calc)+")"+
    (p._second&&p.cltv!=null?" · CLTV "+cltvText(p.cltv):"")+(lg.stale?" — re-check: DOB / value / balance changed":"");
  return p;
}

// ── LESA ───────────────────────────────────────────────────────────────
// SmartFi Choice never allows a LESA, and neither does HomeSafe Second. Gated
// on the toggle itself (not on the dollar amount), so the products vanish the
// moment LESA is switched on, before any taxes are typed.
var LESA_BLOCKED={choiceFixed:"No LESA on SmartFi Choice",choiceFlex:"No LESA on SmartFi Choice",hs2:"HomeSafe Second never allows a LESA"};
export function lesaGate(family,lesaOn){return lesaOn?(LESA_BLOCKED[family]||null):null;}

// ── Longbridge Platinum fixed tiers ────────────────────────────────────
// One sheet key carries four products. "rate:orig[:Name]" — an explicit name
// wins; otherwise: 8.49@4 → A, 1% orig → Max, the SECOND 2%-orig tier →
// Preserve (the 9/25 sheet lists Peak 9.99@2 first and Preserve 9.75@2 last),
// anything else → Peak.
export function platinumTiers(tiersStr){
  var seen2=0;
  return String(tiersStr||"").split("/").map(function(t){
    var parts=String(t).split(":");var rate=parseFloat(parts[0]);var orig=parseFloat(parts[1])||0;var nm=String(parts[2]||"").trim();
    if(isNaN(rate))return null;
    var tier;
    if(/^(A|Peak|Max|Preserve)$/i.test(nm))tier=nm.charAt(0).toUpperCase()+nm.slice(1).toLowerCase();
    else if(Math.abs(rate-8.49)<1e-9&&Math.abs(orig-4)<1e-9)tier="A";
    else if(Math.abs(orig-1)<1e-9)tier="Max";
    else if(Math.abs(orig-2)<1e-9){seen2++;tier=seen2>1?"Preserve":"Peak";}
    else tier="Peak";
    return{rate:rate,orig:orig,tier:tier};
  }).filter(Boolean);
}
// Origination (to Longbridge): Max = greater of $5K or 1% of PL, Preserve =
// greater of $5K or 2% (doc 45 p1, doc 39 p3/p6). The 9/25/26 Broker Gold
// Plus sheet (newest wins) caps "Peak, Max, Preserve & NY Fixed" at $30,000
// and says "Platinum A: No Dollar Cap" — so every tier but A is capped.
export function platinumOrig(tier,gross,origPct){
  var pctAmt=(Number(gross)||0)*(Number(origPct)||0)/100;
  if(tier==="A")return pctAmt;
  if(tier==="Max"||tier==="Preserve")pctAmt=Math.max(5000,pctAmt);
  return Math.min(30000,pctAmt);
}
